import type { JSX } from "react";
import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { ulid } from "ulid";
import { toast } from "sonner";
import { Check, Plus, X } from "lucide-react";
import type { RecipeTaxonomyRefs, TaxonomyApiItem, TaxonomyKind } from "@recipevault/shared";
import { db } from "../../sync/db.js";
import { createTaxonomy } from "../taxonomy/api.js";
import { friendlyError } from "../../shared/http.js";
import { cn } from "../../shared/utils.js";
import { KIND_CHIP_CLASS } from "./taxonomyKindStyle.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/popover.js";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/command.js";

interface Props {
  value: RecipeTaxonomyRefs;
  onChange: (next: RecipeTaxonomyRefs) => void;
}

interface RowProps {
  kind: TaxonomyKind;
  label: string;
  singular: string; // for "Add <singular>" button + create prompt
  multi: boolean;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

function KindRow({ kind, label, singular, multi, selectedIds, onSelectionChange }: RowProps): JSX.Element {
  const items = useLiveQuery(
    async () => {
      const all = await db.taxonomy.where("kind").equals(kind).toArray();
      return all.filter((i) => !i.deletedAt);
    },
    [kind],
    [] as TaxonomyApiItem[],
  );
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);

  const trimmed = search.trim();
  const itemById = new Map(items.map((i) => [i.id, i] as const));
  const exactMatch = items.some((i) => i.name.toLowerCase() === trimmed.toLowerCase());
  const canCreate = trimmed.length > 0 && !exactMatch && !creating;

  function applySelection(nextIds: string[]): void {
    onSelectionChange(nextIds);
  }

  function toggle(id: string): void {
    if (multi) {
      applySelection(
        selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id],
      );
    } else {
      applySelection(selectedIds.includes(id) ? [] : [id]);
      setOpen(false);
    }
  }

  async function create(): Promise<void> {
    if (!canCreate) return;
    setCreating(true);
    try {
      const { item } = await createTaxonomy({ kind, name: trimmed }, ulid());
      await db.taxonomy.put(item);
      if (multi) {
        applySelection([...selectedIds, item.id]);
      } else {
        applySelection([item.id]);
      }
      setSearch("");
      if (!multi) setOpen(false);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setCreating(false);
    }
  }

  const selectedItems = selectedIds
    .map((id) => itemById.get(id))
    .filter((i): i is TaxonomyApiItem => i !== undefined);

  return (
    <div className="flex items-baseline gap-2 flex-wrap">
      <div className="text-[0.6875rem] font-medium uppercase tracking-wider text-zinc-500 w-20 shrink-0">
        {label}
      </div>
      <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
        {selectedItems.map((item) => (
          <span
            key={item.id}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border pl-2.5 pr-1 py-0.5 text-xs",
              KIND_CHIP_CLASS[kind],
            )}
          >
            {item.name}
            <button
              type="button"
              onClick={() => { toggle(item.id); }}
              className="rounded-full hover:bg-black/30 p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
              aria-label={`Remove ${item.name}`}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 border border-dashed border-zinc-700 hover:border-zinc-500 rounded-full px-2.5 py-0.5 outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
            >
              <Plus className="h-3 w-3" aria-hidden />
              {multi || selectedIds.length === 0 ? `Add ${singular}` : `Change ${singular}`}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput
                placeholder={`Search or create ${singular}…`}
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                <CommandEmpty>
                  {canCreate ? null : "No matching items."}
                </CommandEmpty>
                <CommandGroup>
                  {items.map((item) => {
                    const isSelected = selectedIds.includes(item.id);
                    return (
                      <CommandItem
                        key={item.id}
                        value={item.name}
                        onSelect={() => { toggle(item.id); }}
                      >
                        <span
                          className={cn(
                            "mr-2 inline-flex h-3 w-3 items-center justify-center",
                            isSelected ? "text-emerald-400" : "text-transparent",
                          )}
                          aria-hidden
                        >
                          <Check className="h-3 w-3" />
                        </span>
                        {item.name}
                      </CommandItem>
                    );
                  })}
                  {canCreate && (
                    <CommandItem
                      value={trimmed}
                      onSelect={() => { void create(); }}
                      className="text-emerald-300"
                    >
                      <Plus className="mr-2 h-3 w-3" aria-hidden />
                      Create &ldquo;{trimmed}&rdquo;
                    </CommandItem>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export function TaxonomyPicker({ value, onChange }: Props): JSX.Element {
  return (
    <div className="space-y-1.5">
      <KindRow
        kind="tag"
        label="Tags"
        singular="tag"
        multi
        selectedIds={value.tagIds}
        onSelectionChange={(ids) => { onChange({ ...value, tagIds: ids }); }}
      />
      <KindRow
        kind="category"
        label="Categories"
        singular="category"
        multi
        selectedIds={value.categoryIds}
        onSelectionChange={(ids) => { onChange({ ...value, categoryIds: ids }); }}
      />
      <KindRow
        kind="cuisine"
        label="Cuisine"
        singular="cuisine"
        multi={false}
        selectedIds={value.cuisineId ? [value.cuisineId] : []}
        onSelectionChange={(ids) => { onChange({ ...value, cuisineId: ids[0] ?? null }); }}
      />
      <KindRow
        kind="diet"
        label="Diet"
        singular="diet"
        multi
        selectedIds={value.dietFlags}
        onSelectionChange={(ids) => { onChange({ ...value, dietFlags: ids }); }}
      />
    </div>
  );
}
